/****************************************************
 * Module Name:  manufacturingBidByPlan.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState } from "react";
import { Box, Tooltip, Stack } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import EditIcon from '@mui/icons-material/Edit';
import Constants from "../../../utility/constants";
import Utility from "../../../utility/utility";
import renderCellExpand from "../../common/dataGridCellExpand";
import TenderLotBidProxy from "../../../utility/proxy/manager/tender/tenderLotBidProxy";
import NivodaProxy from "../../../utility/proxy/manager/nivodaProxy";
import { LoadingButton } from "@mui/lab";
import { IoIosLock } from "react-icons/io";
import CommonSnackbar from "../../common/commonSnackBar";
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import PricePlanningProxy from "../../../utility/proxy/manager/manufacturingProxy/pricePlanningProxy";

function getNivodaStockByStoneId(stoneid) {
    return new Promise((resolve, reject) => {
        try {
            NivodaProxy.GetNivodaStockByStoneId(stoneid, (responseData) => {
                resolve(responseData);
            });
        } catch (error) {
            console.error('Error fetching products', error);
            reject(error); // Reject the Promise if an error occurs
        }
    });
}

const ManufacturingBidByPlan = ({
    tenderid,
}) => {
    const { apiRef, handleCellClick } = Utility.useSingleClickEdit();
    const [listData, setListData] = useState([]);
    const [filterValues, setFilterValues] = useState({});
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [selectionModel, setSelectionModel] = useState(new Map());
    const [outerLoader, setOuterLoader] = useState();
    const [lockLoader, setLockLoader] = useState(false);
    const [columnNames, setColumnNames] = React.useState([]);

    useEffect(() => {
        if (tenderid)
            handleSearch();
    }, []);

    const handleSearch = () => {
        setOuterLoader(true);
        TenderLotBidProxy.GetTenderLotPricePlanDataByTenderId(tenderid, "false", (responseData) => {
            setOuterLoader(true);
            if (responseData) {

                const transformedData = responseData.map(item => {
                    const rapnetPrice = parseFloat(item.rapnetprice);
                    const ppc = (rapnetPrice + (rapnetPrice * (item.sellbiddisc / 100))).toFixed(2);
                    const stoneprice = (parseFloat(ppc) * parseFloat(item.size)).toFixed(2);
                    let total = stoneprice;

                    if (item.iscertified && item.labprice != null) {
                        total = (parseFloat(total) - parseFloat(item.labprice)).toFixed(2);
                    }
                    return {
                        ...item,
                        stonetype: item.stonetype === "L" ? "Lab Grown Diamonds" : "Natural Diamonds",
                        rapnetprice: item.colortype === "WHITE" ? item.currentrapnetprice : item.rapnetprice,
                        mfgbiddisc: item.sellbiddisc,
                        ppc: ppc,
                        stoneprice: stoneprice,
                        total: total
                    }
                });

                const uniqueFilterValues = {
                    createdbyusername: Utility.getDistinctOptions(transformedData, "createdbyusername"),
                    shape: Utility.getDistinctOptions(transformedData, "shape"),
                    color: Utility.getDistinctOptions(transformedData, "color"),
                    cut: Utility.getDistinctOptions(transformedData, "cut"),
                    pol: Utility.getDistinctOptions(transformedData, "pol"),
                    sym: Utility.getDistinctOptions(transformedData, "sym"),
                    clarity: Utility.getDistinctOptions(transformedData, "clarity"),
                    flo: Utility.getDistinctOptions(transformedData, "flo"),
                    stonetype: Utility.getDistinctOptions(transformedData, "stonetype"),
                    lockedbyusername: Utility.getDistinctOptions(transformedData, "lockedbyusername"),
                    status: Utility.getDistinctOptions(transformedData, "status"),
                    lab: Utility.getDistinctOptions(transformedData, "lab"),
                    colortype: Utility.getDistinctOptions(transformedData, "colortype"),
                    fancycolor1: Utility.getDistinctOptions(transformedData, "fancycolor1"),
                    fancycolor2: Utility.getDistinctOptions(transformedData, "fancycolor2"),
                    fromintensity: Utility.getDistinctOptions(transformedData, "fromintensity"),
                    tointensity: Utility.getDistinctOptions(transformedData, "tointensity"),
                    overtone: Utility.getDistinctOptions(transformedData, "overtone"),
                };

                let tempResponseData = [];
                try {
                    tempResponseData = transformedData.map(iResponse => {

                        if (iResponse.nivodastonepricing && iResponse.nivodastonepricing.priceList) {

                            iResponse.nivodastonepricing.priceList.forEach((iPriceList, index) => {

                                iResponse[`${index + 1}`] =
                                    iPriceList.discount + ' | ' + iPriceList.price + ' (' + iPriceList.noOfStones + ')';
                            });
                        }

                        return iResponse;
                    });

                } catch (error) {
                    console.error("Error processing response data:", error);
                }
                setFilterValues(uniqueFilterValues);
                prepareColumns(uniqueFilterValues);
                setOuterLoader(false);
                setListData(tempResponseData);
            } else {
                setOuterLoader(false);
                setSnackbarState(true, Constants.APiFailErrorMessage, "error");
            }
        })
    }


    const prepareColumns = (uniqueFilterValues) => {
        const columnNames = columnsWithoutPermission.map(woPermissionColumn => {
            let guids = [];

            // Determine which GUIDs (permissions) should be added based on the column field
            switch (woPermissionColumn.field) {
                case "discount":
                    guids = [Constants.COLUMN_PERMISSION.MFG_SELL_BID_DISC];
                    break;
                default:
                    guids = [];
            }

            // Retrieve valid permission columns
            return fetchColumnDataFromGuid(guids, woPermissionColumn, columnsWithPermission, uniqueFilterValues);

        }).flat(); // Flatten the array

        // Update state with the prepared columns
        setColumnNames(columnNames);
    };

    const fetchColumnDataFromGuid = (guids, currentColumn, columnsWithPermission, uniqueFilterValues) => {
        const permissionColumns = guids
            .map(id => Constants.USER_COLUMN_PERMISSION.find(permission => permission === id)) // Match permissions
            .filter(Boolean) // Remove undefined values (invalid permissions)
            .map(guid => {
                const columnData = columnsWithPermission[guid]; // Get column details

                // If the columnData is missing or an empty object, skip adding it
                if (!columnData || Object.keys(columnData).length === 0) {
                    return null;
                }

                // Retrieve unique filter values for the column, defaulting to an empty array
                const filterData = uniqueFilterValues[columnData.field] || [];

                return {
                    ...columnData,  // Copy all existing column properties
                    valueOptions: filterData,  // Assign filter values
                };
            })
            .filter(Boolean); // Remove any null values from the list

        // Modify the original column before adding it to the list
        const modifiedColumn = {
            ...currentColumn,  // Retain original column properties
            valueOptions: uniqueFilterValues[currentColumn.field] || [], // Assign filter values
        };

        // Return an array with the modified column and any valid permission columns
        return [modifiedColumn, ...permissionColumns];
    };

    const setSnackbarState = (show, message, variant) => {
        setShowSnackbar(show);
        setSnackbarMessage(message);
        setSnackbarVariant(variant);
    };

    const handleProcessRowUpdateError = (error) => {
        // console.error(error);
    };

    const getChangedFields = (newRow, oldRow) => {
        const changedFields = [];
        Object.keys(newRow).forEach(field => {
            if (newRow[field] !== oldRow[field]) {
                changedFields.push({
                    id: newRow.id,
                    field: field,
                    value: newRow[field]
                });
            }
        });

        return changedFields;
    };

    const addToSelectionModel = (id) => {
        let newSelectionModel = new Set(selectionModel);
        newSelectionModel.add(id);
        setSelectionModel(Array.from(newSelectionModel));

    }

    const removeFromSelectionModel = (id) => {
        let newSelectionModel = new Set(selectionModel);
        newSelectionModel.remove(id);
        setSelectionModel(Array.from(newSelectionModel));

    }

    const incrementDecrementDiscount = (params, isIncr) => {

        setListData(listData => listData.map(iRow => {
            if (iRow.id === params.id) {

                let mfgbiddisc = iRow.mfgbiddisc ? parseFloat(iRow.mfgbiddisc) : 0

                mfgbiddisc = mfgbiddisc + (isIncr ? 1 : -1);

                if (iRow.currentrapnetprice !== null) {
                    const rapnetPrice = parseFloat(iRow.currentrapnetprice);

                    const ppc = (rapnetPrice + (rapnetPrice * (mfgbiddisc / 100))).toFixed(2);
                    const stoneprice = (parseFloat(ppc) * parseFloat(iRow.size)).toFixed(2);
                    let total = stoneprice;

                    if (iRow.iscertified && iRow.labprice != null) {
                        total = (parseFloat(total) - parseFloat(iRow.labprice)).toFixed(2);
                    }

                    const newRow = {
                        ...iRow,
                        mfgbiddisc: mfgbiddisc,
                        ppc: ppc,
                        stoneprice: stoneprice,
                        total: total,
                    };

                    return newRow;
                } else {
                    setSnackbarState(true, "Rapnet Data not found for id: [" + iRow.id + "]", "warning");
                    return iRow;
                }
            }
            return iRow;
        }));
    }

    const processRowUpdate = (newR, oldRow, rowsx) => {
        const changedFeilds = getChangedFields(newR, oldRow);

        if (changedFeilds.length > 0) {
            const { id, field, value } = changedFeilds[0];

            const updatedRow = rowsx.map(iRow => {
                if (iRow.id === id) {
                    if (field === "mfgbiddisc" && parseFloat(value) !== parseFloat(iRow.mfgbiddisc)) {
                        const discount = parseFloat(value.toFixed(Constants.DEFAULT_NO_OF_DIGITS));
                        if (iRow.rapnetprice !== null) {
                            const rapnetPrice = parseFloat(iRow.rapnetprice);

                            const ppc = (rapnetPrice + (rapnetPrice * (discount / 100))).toFixed(2);
                            const stoneprice = (parseFloat(ppc) * parseFloat(iRow.size)).toFixed(2);
                            let total = stoneprice;

                            if (iRow.iscertified && iRow.labprice != null) {
                                total = (parseFloat(total) - parseFloat(iRow.labprice)).toFixed(2);
                            }
                            const newRow = {
                                ...iRow,
                                [field]: discount,
                                ppc: ppc,
                                stoneprice: stoneprice,
                                total: total,
                            };

                            addToSelectionModel(id);

                            return newRow;
                        } else {
                            setSnackbarState(true, "Rapnet Data not found for id: [" + iRow.id + "]", "warning");

                            const newRow = {
                                ...iRow,
                                [field]: discount,
                            };

                            removeFromSelectionModel(id);
                            return newRow;
                        }
                    }
                    else if (field === "ppc" && parseFloat(value) !== parseFloat(iRow.ppc)) {
                        const ppc = parseFloat(value.toFixed(Constants.DEFAULT_NO_OF_DIGITS));
                        if (iRow.colortype === "WHITE") {
                            if (iRow.currentrapnetprice !== null) {
                                const mfgbiddisc = parseFloat(((((ppc * iRow.size) / (iRow.currentrapnetprice * iRow.size)) * 100) - 100).toFixed(6));
                                const stoneprice = parseFloat((ppc * iRow.size).toFixed(Constants.DEFAULT_NO_OF_DIGITS));
                                let total = stoneprice;
                                if (iRow.iscertified && iRow.labprice != null) {
                                    total = (parseFloat(total) - parseFloat(iRow.labprice)).toFixed(2);
                                }

                                const newRow = {
                                    ...iRow,
                                    [field]: ppc,
                                    mfgbiddisc: mfgbiddisc,
                                    stoneprice: stoneprice,
                                    total: total,
                                };

                                addToSelectionModel(id);
                                return newRow;
                            } else {
                                setSnackbarState(true, "Rapnet Data not found for id: [" + iRow.id + "]", "warning");
                                return iRow;
                            }
                        } else {
                            const stoneprice = parseFloat((ppc * iRow.size).toFixed(Constants.DEFAULT_NO_OF_DIGITS));
                            let total = parseFloat(stoneprice.toFixed(2));
                            if (iRow.iscertified && iRow.labprice != null) {
                                total = (parseFloat(total) - parseFloat(iRow.labprice)).toFixed(2);
                            }

                            const newRow = {
                                ...iRow,
                                [field]: ppc,
                                rapnetprice: ppc,
                                stoneprice: stoneprice,
                                total: total,
                            };

                            addToSelectionModel(id);
                            return newRow;
                        }
                    }
                    else if (field === "total" && parseFloat(value) !== parseFloat(iRow.total)) {
                        const total = parseFloat(value.toFixed(Constants.DEFAULT_NO_OF_DIGITS));
                        if (iRow.colortype === "WHITE") {
                            if (iRow.currentrapnetprice !== null) {
                                let stoneprice = total;
                                if (iRow.iscertified && iRow.labprice != null) {
                                    stoneprice = (parseFloat(stoneprice) + parseFloat(iRow.labprice)).toFixed(2);
                                }
                                const ppc = parseFloat((stoneprice / iRow.size).toFixed(Constants.DEFAULT_NO_OF_DIGITS));
                                const mfgbiddisc = parseFloat(((((ppc * iRow.size) / (iRow.currentrapnetprice * iRow.size)) * 100) - 100).toFixed(6));
                                const newRow = {
                                    ...iRow,
                                    [field]: total,
                                    mfgbiddisc: mfgbiddisc,
                                    stoneprice: stoneprice,
                                    ppc: ppc,
                                };

                                addToSelectionModel(id);
                                return newRow;
                            } else {
                                setSnackbarState(true, "Rapnet Data not found for id: [" + iRow.id + "]", "warning");
                                return iRow;
                            }
                        } else {
                            let stoneprice = total;
                            if (iRow.iscertified && iRow.labprice != null) {
                                stoneprice = (parseFloat(stoneprice) + parseFloat(iRow.labprice)).toFixed(2);
                            }
                            const ppc = parseFloat((stoneprice / iRow.size).toFixed(Constants.DEFAULT_NO_OF_DIGITS));
                            const newRow = {
                                ...iRow,
                                [field]: total,
                                rapnetprice: ppc,
                                stoneprice: stoneprice,
                                ppc: ppc,
                            };

                            addToSelectionModel(id);
                            return newRow;
                        }
                    }
                }
                return iRow;
            });

            setListData(updatedRow);
            return updatedRow.find(row => row.id === id) || oldRow;
        } else {
            return { ...newR };
        }
    }

    const lockDiscount = () => {
        setLockLoader(true);
        let lockData = [];
        let bidData = [];
        let ids = selectionModel;

        if (ids.length > 0) {
            ids.forEach(selectedRow => {
                const matchingDetail = listData.find((detail) => detail.id === selectedRow);

                lockData.push({
                    id: selectedRow,
                    discount: matchingDetail.discount,
                    ppc: matchingDetail.ppc ? matchingDetail.ppc : 0
                })

                if (matchingDetail) {
                    bidData.push({
                        tenderstoneid: matchingDetail.tenderstoneid,
                        mfgbiddisc: matchingDetail.mfgbiddisc ? matchingDetail.mfgbiddisc : 0,
                        priceplanid: selectedRow,
                        tenderlotid: matchingDetail.tenderlotid,
                        mfgbidlockedby: Constants.loggedInUser.id,
                    });
                }
            });
        }

        if (lockData.length > 0) {
            PricePlanningProxy.lockDiscount(Constants.loggedInUser.id, lockData, (response) => {
                if (response) {
                    if (response.status === "Success") {
                        setSnackbarState(true, "Discount Locked Successfully", "success");
                        handleSearch();
                    } else if (response.status === "Warning") {
                        setSnackbarState(true, response.errorMessage, "warning");
                    }
                } else {
                    setSnackbarState(true, Constants.APiFailErrorMessage, "error");
                }
            })
        }


        if (bidData.length > 0) {
            TenderLotBidProxy.MakeManufacturingBid(bidData, (response) => {
                if (response) {
                    setShowSnackbar(true);
                    setSnackbarMessage("Bid Successfully Made");
                    setSnackbarVariant("success");
                    handleSearch();
                } else {
                    setShowSnackbar(true);
                    setSnackbarMessage(Constants.APiFailErrorMessage);
                    setSnackbarVariant("error");
                }
            })
        }
        setLockLoader(false);
    }

    const columnsWithPermission = {
        "92220e56-ce36-48ea-b8c7-5ef94430eb42": {
            field: 'sellbiddisc',
            headerName: 'SELL BID DIS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            width: 100,
            editable: false,
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value !== undefined && params.value !== null
                            ? parseFloat(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
                            : '0.00'}
                    </span>
                </div>
            ),
            cellClassName: (params) => 'lavenderCell'
        },
    };

    const columnsWithoutPermission = [
        {
            field: 'id',
            headerName: 'ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 50
        },
        {
            field: 'shape',
            headerName: 'SHAPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.shape : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 123
        },
        {
            field: 'size',
            headerName: 'CTS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 76
        },
        {
            field: 'colortype',
            headerName: 'COL TYPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.colortype : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'color',
            headerName: 'COL',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.color : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 30
        },
        {
            field: 'fancycolor1',
            headerName: 'FANCY COLOR 1',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.fancycolor1 : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 115
        },
        {
            field: 'fancycolor2',
            headerName: 'FANCY COLOR 2',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.fancycolor2 : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 115
        },
        {
            field: 'fromintensity',
            headerName: 'FROM INTENSITY',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.fromintensity : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 125
        },
        {
            field: 'tointensity',
            headerName: 'TO INTENSITY',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.tointensity : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 100
        },
        {
            field: 'overtone',
            headerName: 'OVERTONE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.overtone : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 90
        },
        {
            field: 'clarity',
            headerName: 'CLR',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.clarity : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 60
        },
        {
            field: 'cut',
            headerName: 'CUT',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.cut : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'pol',
            headerName: 'POL',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.pol : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'sym',
            headerName: 'SYM',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.sym : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'flo',
            headerName: 'FLO',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.flo : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 60
        },
        {
            field: 'lab',
            headerName: 'LAB',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.lab : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 50
        },
        {
            field: 'stonetype',
            headerName: 'STONE TYPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueGetter: (params) => params.value,
            valueOptions: filterValues ? filterValues.stonetype : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value,
            width: 130
        },
        {
            field: 'noofstones',
            headerName: 'STONES',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 75,
            renderHeader: () => (
                <Tooltip title="NO. OF STONES" placement="bottom">
                    <span>STONES</span>
                </Tooltip>
            ),
        },
        {
            field: 'discount',
            headerName: 'DIS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            width: 80,
            editable: false,
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value !== undefined && params.value !== null ?
                            parseFloat(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS) : '0.00'}
                    </span>
                </div>
            ),
            cellClassName: (params) => 'lavenderCell'
        },
        {
            field: " ",
            width: 5,
            editable: false,
            renderCell: (params) => params.row.colortype === "WHITE" ? (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {<RemoveCircleIcon style={{
                        marginLeft: 3, marginBottom: 3, cursor: 'pointer', fontSize: "1.5rem", color: "#d40b00"
                    }}
                        onClick={() => incrementDecrementDiscount(params, false)}
                    />}
                </div>
            ) : null,
            disabled: false
        },
        {
            field: 'mfgbiddisc',
            headerName: 'MFG BID DIS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            width: 100,
            editable: true,
            valueFormatter: (params) => params.value,
            renderCell: (params) => params.row.colortype === "WHITE" ? (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value !== undefined && params.value !== null ?
                            parseFloat(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS) : '0.00'}
                    </span>
                    {params.colDef.editable && <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />}
                </div>
            ) : (params.value ? (params.value).toFixed(2) : '0.00'),
            cellClassName: (params) => params.row.colortype === "WHITE" ? 'lavenderCell' : 'cursorNA'
        },
        {
            field: "",
            width: 5,
            editable: false,
            renderCell: (params) => params.row.colortype === "WHITE" ? (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {<AddCircleIcon style={{
                        marginLeft: 3, marginBottom: 3, fontSize: '1.5rem', cursor: 'pointer', color: "#51874d"
                    }}
                        onClick={() => incrementDecrementDiscount(params, true)}
                    />}
                </div>
            ) : null,
            disabled: false
        },
        {
            field: 'rapnetprice',
            headerName: 'RAP',
            headerAlign: 'center',
            align: 'right',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 60
        },
        {
            field: 'ppc',
            headerName: 'PPC',
            headerAlign: 'center',
            align: 'right',
            type: 'number',
            editable: true,
            width: 110,
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value !== undefined && params.value !== null
                            ? parseFloat(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
                            : params.value}
                    </span>
                    {params.colDef.editable && <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />}
                </div>
            ),
            cellClassName: (params) => 'lavenderCell'
        },
        {
            field: 'labprice',
            headerName: 'LAB $',
            headerAlign: 'center',
            align: 'right',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'stoneprice',
            headerName: 'STONE $',
            headerAlign: 'center',
            align: 'right',
            type: 'number',
            valueFormatter: (params) => params.value,
            width: 80
        },
        {
            field: 'total',
            headerName: 'TOTAL $',
            headerAlign: 'center',
            align: 'right',
            type: 'number',
            width: 110,
            editable: true,
            valueFormatter: (params) => params.value,
            renderHeader: () => (
                <Tooltip title="STONE $ - LAB $" placement="bottom">
                    <span>TOTAL $</span>
                </Tooltip>
            ),
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value !== undefined && params.value !== null
                            ? parseFloat(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
                            : params.value}
                    </span>
                    {params.colDef.editable && <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />}
                </div>
            ),
            cellClassName: (params) => 'lavenderCell'
        },
        {
            field: 'finaltotal',
            headerName: 'FINAL $',
            headerAlign: 'center',
            align: 'right',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: (params) => Number(params.row.total * params.row.noofstones).toFixed(Constants.DEFAULT_NO_OF_DIGITS) || "",
            width: 75,
            renderHeader: () => (
                <Tooltip title="TOTAL $ * NO. OF STONES" placement="bottom">
                    <span>FINAL $</span>
                </Tooltip>
            ),
        },
        ...Array.from({ length: 11 }, (_, index) => ({
            field: `${index + 1}`,
            headerName: `${index + 1}`,
            headerAlign: "center",
            align: "right",
            width: 115,
            renderCell: (params) => {
                return params.value
            },
            cellClassName: (params) => {
                const discountValue = params.row.discount;
                if (discountValue !== undefined && discountValue !== null && params.value && parseFloat((params.value.split("|")[0]).trim()) === parseFloat(discountValue)) {
                    return "darkBlueCell";
                }
                return "";
            }
        })),
        {
            field: 'createdby',
            headerName: 'CREATED BY',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 92
        },
        {
            field: 'createdbyusername',
            headerName: 'CREATED BY USERNAME',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.createdbyusername : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 150
        },
        {
            field: 'createdat',
            headerName: 'CREATED AT',
            headerAlign: 'center',
            align: 'center',
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            },
            width: 150
        },
        {
            field: 'updatedat',
            headerName: 'UPDATED AT',
            headerAlign: 'center',
            align: 'center',
            width: 150,
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            }
        },
        {
            field: 'status',
            headerName: 'STATUS',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.status : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 100
        },
        {
            field: 'iscertified',
            headerName: 'IS CERTIFIED',
            headerAlign: 'center',
            align: 'center',
            type: "boolean",
            width: 100
        },
    ];

    const getInnerColumns = (innerFilterValues) => [
        {
            field: 'id',
            headerName: 'ID',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 50
        },
        {
            field: 'shape',
            headerName: 'SHAPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.shape : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 122
        },
        {
            field: 'carat',
            headerName: 'SIZE',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'color',
            headerName: 'COLOR',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.color : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 65
        },
        {
            field: 'clarity',
            headerName: 'CLARITY',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.clarity : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 75
        },
        {
            field: 'cut',
            headerName: 'CUT',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.cut : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 50
        },
        {
            field: 'pol',
            headerName: 'POL',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.pol : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'sym',
            headerName: 'SYM',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.sym : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 40
        },
        {
            field: 'flo',
            headerName: 'FLO',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.flo : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 80
        },
        {
            field: 'stonetype',
            headerName: 'STONE TYPE',
            headerAlign: 'center',
            align: 'center',
            type: "singleSelect",
            valueGetter: (params) => params.value === "L" ? "Lab Grown Diamonds" : "Natural Diamonds",
            valueOptions: innerFilterValues ? innerFilterValues.stonetype : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            width: 150
        },
        {
            field: 'discount',
            headerName: 'DIS',
            headerAlign: 'center',
            align: 'center',
            type: 'number',
            width: 80,
            valueFormatter: (params) => params.value,
        },
        {
            field: "priceperct",
            headerName: "PPC",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 60,
            renderCell: (params) => Number(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
        },
        {
            field: "diamondprice",
            headerName: "TOTAL",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 110,
            renderCell: (params) => Number(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
        },
        {
            field: "location",
            headerName: "LOC",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "singleSelect",
            valueOptions: innerFilterValues ? innerFilterValues.location : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "position",
            headerName: "POSITION",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 100,
            renderCell: renderCellExpand
        },
    ];

    const getInnerDetailPanelHeight = React.useCallback(() => 'auto', []);

    const getInnerDetailPanelContent = React.useCallback((row) => <DetailPanelInnerContent row={row} />,
        [],);

    function DetailPanelInnerContent({ row: rowProp, innerColumns1 }) {
        const [innerFilterValues, setInnerFilterValues] = useState({});
        let innerColumns = getInnerColumns(innerFilterValues);
        const [innerRowDetails, setInnerRowsDetails] = React.useState([]);
        const [innerLoader, setInnerLoader] = useState(true);

        let response;

        React.useEffect(() => {
            let isMounted = true;

            (async () => {
                response = await getNivodaStockByStoneId(rowProp.id);
                if (response) {
                    const uniqueFilterValues = {
                        shape: Utility.getDistinctOptions(response, "shape"),
                        color: Utility.getDistinctOptions(response, "color"),
                        cut: Utility.getDistinctOptions(response, "cut"),
                        pol: Utility.getDistinctOptions(response, "pol"),
                        sym: Utility.getDistinctOptions(response, "sym"),
                        clarity: Utility.getDistinctOptions(response, "clarity"),
                        flo: Utility.getDistinctOptions(response, "flo"),
                        stonetype: Utility.getDistinctOptions(response, "stonetype"),
                        status: Utility.getDistinctOptions(response, "status"),
                        location: Utility.getDistinctOptions(response, "location"),
                    };
                    setInnerFilterValues(uniqueFilterValues);

                    let position = 1;
                    let responseData = response
                        .map(iResponse => ({ ...iResponse, diamondprice: Math.round(iResponse.diamondprice) }))
                        .sort((a, b) => a.diamondprice - b.diamondprice)
                        .map((iData, index, array) => {
                            if (index === 0 || iData.diamondprice === array[index - 1].diamondprice) {
                                iData.position = position;
                            } else {
                                iData.position = ++position;
                            }
                            return iData;
                        });
                    setInnerRowsDetails(responseData);
                }
                if (isMounted) {
                    setInnerLoader(false);
                }
            })();

            return () => {
                isMounted = false;
            };
        }, [rowProp.id]);

        return (
            <Stack
                sx={{ py: 2, height: '100%', boxSizing: 'border-box' }}
                direction="column"
            >
                <Box
                    sx={{
                        backgroundColor: '#fff',
                        color: '#000',
                        width: "58vw",
                        position: "relative",
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "4px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .MuiTablePagination-toolbar, .MuiDataGrid-footerContainer": {
                            minHeight: "30px"
                        },
                        "& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                            margin: 0
                        },
                        height: 'auto',
                        marginLeft: '16px',
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick={true}
                        density="compact"
                        columns={innerColumns}
                        rows={innerRowDetails}
                        getRowId={(row) => row.id}
                        loading={innerLoader}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "8px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "15px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "22px",
                            },

                            fontSize: Constants.defaultTableFontSize, fontWeight: 'bold',
                            maxHeight: '30vh',
                        }}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    id: false,
                                    createdby: false,
                                    createdbyusername: false,
                                    lockedby: false,
                                    status: false,
                                    rapnetdate: false,
                                    updatedat: false,
                                    createdat: false
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                    />
                </Box>
            </Stack>
        );
    }

    return (
        <div>
            <div>
                <CommonSnackbar
                    show={showSnackbar}
                    message={snackbarMessage}
                    variant={snackbarVariant}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={snackbarVariant === "success" ? "Success" :
                        (snackbarVariant === "error" ? "Failed" : "Warning")}
                    handleClose={() => setShowSnackbar(false)}
                />
                <Box
                    sx={{
                        px: 1,
                        width: "100%",
                        position: "relative",
                        backgroundColor: '#fff',
                        color: '#000',
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "4px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-znmrd6-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "5px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        height: "69vh",
                    }}
                >
                    <DataGridPro
                        disableRowSelectionOnClick={true}
                        density="compact"
                        columns={columnNames}
                        rows={listData}
                        getRowId={(row) => row.id}
                        loading={outerLoader}
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "8px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "15px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "22px",
                            },

                            "& .lavenderCell": {
                                color: "#080808",
                                backgroundColor: "#D8BFD8",
                            },
                            "& .darkBlueCell": {
                                color: "#080808",
                                backgroundColor: "#A1E3F9",
                            },
                            "& .cursorNA": {
                                pointerEvents: "none !important",
                            },
                            fontSize: Constants.defaultTableFontSize, fontWeight: 'bold',
                        }}
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        checkboxSelection={listData.length > 0}
                        rowSelectionModel={selectionModel}
                        onRowSelectionModelChange={(newSelectionModel) => {
                            setSelectionModel(newSelectionModel);
                        }}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                    id: false,
                                    createdby: false,
                                    createdbyusername: false,
                                    lockedby: false,
                                    status: false,
                                    rapnetdate: false,
                                    updatedat: false,
                                    createdat: false
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                        processRowUpdate={(newR, oldRow) => processRowUpdate(newR, oldRow, listData)}
                        onProcessRowUpdateError={handleProcessRowUpdateError}
                        getDetailPanelHeight={getInnerDetailPanelHeight}
                        getDetailPanelContent={listData.length === 0 ? null : ({ row }) => getInnerDetailPanelContent(row)}
                        apiRef={apiRef}
                        onCellClick={handleCellClick}
                        editMode="cell"
                    />
                    <Box
                        style={{
                            position: "absolute",
                            top: 3,
                            right: "270px",
                        }}
                    >
                        {outerLoader ? (
                            <CircularProgress size="1rem" />
                        ) : (
                            <Tooltip title="Reload List">
                                <RefreshOutlinedIcon
                                    onClick={tenderid ? () => handleSearch() : undefined}
                                    color={tenderid ? "primary" : "disabled"}
                                    fontSize="medium"
                                    sx={{
                                        cursor: tenderid ? "pointer" : "not-allowed",
                                        opacity: tenderid ? 1 : 0.5,
                                        mr: 1,
                                    }}
                                />
                            </Tooltip>
                        )}
                        <LoadingButton
                            sx={{ m: "0 10px", height: "36px" }}
                            variant="outlined"
                            onClick={() => lockDiscount()}
                            loading={lockLoader}
                            disabled={selectionModel.length === 0 || outerLoader}
                            loadingPosition="start"
                            startIcon={<IoIosLock size={18} />}
                        >
                            LOCK
                        </LoadingButton>
                    </Box>
                </Box>
            </div>
        </div>
    );
}

export default ManufacturingBidByPlan;